import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ConfirmationDialog, Page } from '../../../components';
import Label from '../../../components/label';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import { fCurrency, fNumber } from '../../../utils/formatNumber';
import { fDate } from '../../../utils/formatTime';
import { completeAudit, getAuditItems, reconcileAuditRecord, scanAuditItem } from '../store/auditActions';
import { CAN_MANAGE_AUDITS } from '../../../shared/auth/roles';
import { getProfile } from '../../../shared/auth/authService';

const TABLE_HEAD = [
  { id: 'name', label: 'Description', alignRight: false },
  { id: 'barcode_number', label: 'Barcode#', alignRight: false },
  { id: 'serial_no', label: 'Serial#', alignRight: false },
  { id: 'cost', label: 'Cost', alignRight: false },
  { id: 'expected', label: 'Expected At', alignRight: false },
  { id: 'assigned_to', label: 'AssignedTo', alignRight: false },
  { id: 'audited_on', label: 'Counted On', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
];

const STATUS_COLOUR = {
  PENDING: 'default',
  AUDITED: 'success',
  MISPLACED: 'warning',
  MISSING: 'error',
  UNLISTED: 'info',
};

// What each line means, spelled out — the vocabulary is the whole point of the
// screen and "misplaced" vs "unlisted" is not self-evident.
const STATUS_HELP = {
  PENDING: 'On the checklist, not yet counted',
  AUDITED: 'Counted, and where the register says it should be',
  MISPLACED: 'Counted here, but the register files it at another location',
  MISSING: 'Never counted, and the stock take has been closed',
  UNLISTED: 'Counted here, but not on this location’s checklist at all',
};

const TABS = [
  { value: '', label: 'All' },
  { value: 'scanned', label: 'Counted' },
  { value: 'not_scanned', label: 'Not counted' },
  { value: 'MISPLACED', label: 'Misplaced' },
  { value: 'MISSING', label: 'Missing' },
  { value: 'UNLISTED', label: 'Unlisted' },
];

export default function AuditDetailView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const scannerRef = useRef();
  const { auditItems, auditRecord, itemCount, isLoading } = useSelector((state) => state.auditReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [barcodeNumber, setBarcodeNumber] = useState('');
  const [itemStatus, setItemStatus] = useState('');
  const [selected, setSelected] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  // A short log of the last few scans. A scanner fires faster than a person can
  // read a toast, so what happened stays on screen instead of flashing past.
  const [scanLog, setScanLog] = useState([]);

  const canManage = CAN_MANAGE_AUDITS.includes(getProfile()?.role);
  const isCompleted = Boolean(auditRecord?.audit_completed);

  const fetchItems = useCallback(
    () => dispatch(getAuditItems({ id, page: page + 1, rowsPerPage, itemStatus })),
    [dispatch, id, page, rowsPerPage, itemStatus]
  );

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, value) => {
    setItemStatus(value);
    setPage(0);
    setSelected([]);
  };

  const logScan = (severity, text) =>
    setScanLog((previous) => [{ severity, text, at: Date.now() }, ...previous].slice(0, 6));

  const handleScan = async (value) => {
    const barcode = (value ?? '').trim();
    setBarcodeNumber('');
    if (!barcode || scanning) return;
    if (isCompleted) {
      toast.error('This stock take has been completed');
      return;
    }
    setScanning(true);
    // The server matches the barcode against the checklist and decides whether
    // it is a normal count, a misplacement, or an asset that is not on the list
    // at all. The old screen did that lookup in the browser against a list
    // capped at one page, so anything past the first hundred read as missing.
    const result = await dispatch(scanAuditItem(id, { barcode_number: barcode }));
    setScanning(false);

    if (!result.ok) {
      logScan('error', `${barcode}: ${result.message}`);
      return;
    }
    const item = result.data;
    const name = item?.asset?.name || barcode;
    if (result.body?.counted === false) {
      logScan('info', `${name} was already counted`);
      return;
    }
    logScan(item?.status === 'AUDITED' ? 'success' : 'warning', `${name} — ${result.message}`);
    // Re-read the page so the counts in the header and the row's own status
    // reflect the scan even when the current tab excludes it.
    fetchItems();
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    // Read the value straight off the event: setState does not apply until the
    // next render, so the old handler counted the *previous* barcode.
    handleScan(event.target.value);
  };

  const handleComplete = async () => {
    setSaving(true);
    const result = await dispatch(completeAudit(id));
    setSaving(false);
    if (result.ok) {
      setConfirmComplete(false);
      fetchItems();
    }
  };

  // Only lines that disagree with the register can be resolved by moving an
  // asset, so only those are selectable — MISPLACED (on this checklist, filed
  // elsewhere) and UNLISTED (filed elsewhere, found here anyway). Selecting
  // anything else would be a no-op the user cannot see.
  const isResolvable = (item) => ['MISPLACED', 'UNLISTED'].includes(item.status) && !item.reconciled;
  const reconcilable = useMemo(() => auditItems.filter(isResolvable), [auditItems]);

  const toggleSelected = (itemId) =>
    setSelected((previous) =>
      previous.includes(itemId) ? previous.filter((each) => each !== itemId) : [...previous, itemId]
    );

  const handleReconcile = async (moveAssets) => {
    if (!selected.length) return;
    setSaving(true);
    const result = await dispatch(reconcileAuditRecord(id, { item_ids: selected, move_assets: moveAssets }));
    setSaving(false);
    if (result.ok) {
      setSelected([]);
      fetchItems();
    }
  };

  const total = auditRecord?.total_assets ?? 0;
  const scanned = auditRecord?.total_scanned ?? 0;
  const unscanned = auditRecord?.total_unscanned ?? 0;
  const misplaced = auditRecord?.total_misplaced ?? 0;
  const missing = auditRecord?.total_missing ?? 0;
  const unlisted = auditRecord?.total_unlisted ?? 0;

  return (
    <Page title="Asset Audit">
      <Container maxWidth="xl">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }} elevation={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="h6">{auditRecord?.name}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {auditRecord?.location?.name}
                    {auditRecord?.auditor?.user?.display_name
                      ? ` · ${auditRecord.auditor.user.display_name}`
                      : ''}
                  </Typography>
                </Box>
                <Label color={isCompleted ? 'success' : 'info'}>
                  {(auditRecord?.status || '').replace(/_/g, ' ').toLowerCase()}
                </Label>
              </Stack>

              <LinearProgress
                variant="determinate"
                value={Math.min(auditRecord?.progress ?? 0, 100)}
                sx={{ height: 8, borderRadius: 4, mb: 2 }}
              />

              <Stack direction="row" divider={<Divider orientation="vertical" flexItem />}>
                <Stack width={1} textAlign="center">
                  <Typography variant="subtitle2">ON CHECKLIST</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {fNumber(total)}
                  </Typography>
                </Stack>
                <Stack width={1} textAlign="center">
                  <Typography variant="subtitle2">COUNTED</Typography>
                  <Typography variant="body2" sx={{ color: 'success.main' }}>
                    {fNumber(scanned)}
                  </Typography>
                </Stack>
                <Stack width={1} textAlign="center">
                  <Typography variant="subtitle2">{isCompleted ? 'MISSING' : 'NOT COUNTED'}</Typography>
                  <Typography variant="body2" sx={{ color: 'error.main' }}>
                    {fNumber(isCompleted ? missing : unscanned)}
                  </Typography>
                </Stack>
                <Stack width={1} textAlign="center">
                  <Tooltip title="Counted here, but the register files them elsewhere">
                    <Typography variant="subtitle2">OUT OF PLACE</Typography>
                  </Tooltip>
                  <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    {fNumber(misplaced + unlisted)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {!isCompleted && (
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Asset Scanner" subheader="Scan a barcode, serial or asset tag to count it" />
                <Paper elevation={3} sx={{ p: 2, textAlign: 'center' }}>
                  <Box sx={{ mb: 1 }}>
                    <QrCodeScannerIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                  </Box>
                  <TextField
                    fullWidth
                    name="barcodeNumber"
                    color="primary"
                    placeholder="Scan here or type a barcode and press Enter..."
                    inputRef={scannerRef}
                    value={barcodeNumber}
                    onChange={(event) => setBarcodeNumber(event.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={scanning}
                    focused
                    autoFocus
                  />
                  <Stack spacing={0.5} sx={{ mt: 2, textAlign: 'left' }}>
                    {scanLog.map((entry) => (
                      <Alert key={entry.at} severity={entry.severity} sx={{ py: 0 }}>
                        {entry.text}
                      </Alert>
                    ))}
                  </Stack>
                </Paper>
              </Card>
            </Grid>
          )}

          <Grid item xs={12}>
            <Card>
              <Box sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Typography variant="h6">Checklist</Typography>
                  <Stack direction="row" spacing={1}>
                    {canManage && selected.length > 0 && (
                      <>
                        <LoadingButton
                          size="small"
                          variant="contained"
                          color="warning"
                          loading={saving}
                          onClick={() => handleReconcile(true)}
                        >
                          Move {selected.length} to {auditRecord?.location?.name || 'here'}
                        </LoadingButton>
                        <LoadingButton
                          size="small"
                          variant="outlined"
                          loading={saving}
                          onClick={() => handleReconcile(false)}
                        >
                          Dismiss
                        </LoadingButton>
                      </>
                    )}
                    {!isCompleted && (
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={saving}
                        onClick={() => setConfirmComplete(true)}
                      >
                        Mark as complete
                      </LoadingButton>
                    )}
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                <Tabs value={itemStatus} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                  {TABS.map((tab) => (
                    <Tab key={tab.value} label={tab.label} value={tab.value} />
                  ))}
                </Tabs>
              </Box>

              <CardContent>
                {reconcilable.length > 0 && canManage && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {reconcilable.length} asset(s) on this page were counted here but the register files them
                    elsewhere. Select them to file them where they were found, or dismiss them to close the finding
                    without moving anything.
                  </Alert>
                )}
                <Scrollbar>
                  <TableContainer sx={{ minWidth: 900 }} component={Paper}>
                    <Table size="small">
                      <UserListHead
                        headLabel={canManage ? [{ id: 'select', label: '' }, ...TABLE_HEAD] : TABLE_HEAD}
                        rowCount={auditItems?.length}
                      />
                      <TableBody>
                        {auditItems?.length > 0 ? (
                          auditItems.map((item) => {
                            const asset = item?.asset;
                            const selectable = isResolvable(item);
                            return (
                              <TableRow hover key={item.id} tabIndex={-1} selected={selected.includes(item.id)}>
                                {canManage && (
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={selected.includes(item.id)}
                                      disabled={!selectable}
                                      onChange={() => toggleSelected(item.id)}
                                    />
                                  </TableCell>
                                )}
                                <TableCell align="left">{asset?.name}</TableCell>
                                <TableCell align="left">{asset?.barcode_number}</TableCell>
                                <TableCell align="left">{asset?.serial_no}</TableCell>
                                <TableCell align="left">{fCurrency(asset?.cost)}</TableCell>
                                <TableCell align="left">{item?.expected_branch?.name}</TableCell>
                                <TableCell align="left">{asset?.assigned_to?.user?.display_name}</TableCell>
                                <TableCell align="left">{fDate(item?.audited_on)}</TableCell>
                                <TableCell align="left">
                                  <Tooltip title={STATUS_HELP[item?.status] || ''}>
                                    <Box component="span">
                                      <Label color={STATUS_COLOUR[item?.status] || 'default'}>
                                        {(item?.status || '').toLowerCase()}
                                        {item?.reconciled ? ' · resolved' : ''}
                                      </Label>
                                    </Box>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        ) : (
                          <TableRow>
                            <TableCell align="center" colSpan={TABLE_HEAD.length + 1} sx={{ py: 3 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {isLoading ? 'Loading…' : 'Nothing on this tab.'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Scrollbar>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={itemCount || 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <ConfirmationDialog
        openDialog={confirmComplete}
        handleDialog={() => setConfirmComplete(false)}
        title="Complete this stock take?"
        description={`${unscanned} asset(s) have not been counted. Completing the audit records them as missing, and no further scans can be made.`}
        confirmLabel="Complete"
        loading={saving}
        onConfirm={handleComplete}
      />
    </Page>
  );
}
